// Notifications drawer — the header bell. Surfaces what's awaiting the user:
// quote approvals, expiring licenses (≤30d), failed jobs (24h). Each row
// deep-links. Mirrors INTERACTIONS.md (bell → drawer).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@/components/shared/Icon';
import { apiRequest } from '@/lib/supabase';

interface Note { icon: string; color: string; title: string; sub: string; to: string }

function daysUntil(iso?: string | null): number {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Infinity : Math.ceil((t - Date.now()) / 86_400_000);
}

export function NotificationsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      const [quotes, lics, jobs] = await Promise.all([
        apiRequest<{ data?: any[] }>('/quotes').then((r) => r.data || []).catch(() => []),
        apiRequest<{ data?: any[] }>('/licenses?pageSize=200').then((r) => r.data || []).catch(() => []),
        apiRequest<any[]>('/operations/jobs').then((r) => r || []).catch(() => []),
      ]);
      const out: Note[] = [];
      for (const q of quotes.filter((x: any) => x.status === 'pending-approval' || x.status === 'needs-approval')) {
        out.push({ icon: 'gavel', color: '#7C3AED', title: `Quote ${q.quote_number || q.id} awaits approval`, sub: q.organization_name || '—', to: '/sales/quotes' });
      }
      for (const l of lics.filter((x: any) => x.status === 'active' && daysUntil(x.expires_at) >= 0 && daysUntil(x.expires_at) <= 30).slice(0, 8)) {
        out.push({ icon: 'clock', color: '#D97706', title: `License expires in ${daysUntil(l.expires_at)}d`, sub: `${l.organization?.name || l.org_name || '—'} · ${l.license_id}`, to: `/sales/licenses/${l.id}` });
      }
      for (const j of jobs.filter((x: any) => x.status === 'failed' && Date.now() - new Date(x.reported_at || x.enqueued_at).getTime() <= 86_400_000).slice(0, 8)) {
        out.push({ icon: 'x-circle', color: '#DC2626', title: `${j.op_key} failed`, sub: j.instance_id, to: '/ops/remote-ops' });
      }
      setNotes(out);
      setLoading(false);
    })();
  }, [open]);

  if (!open) return null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(17,24,39,.25)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '92vw', background: 'var(--surface-raised)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--shadow-menu)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Notifications</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><Icon name="x" size={18} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          ) : notes.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <Icon name="check-circle" size={26} color="var(--status-active)" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)' }}>All caught up</div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>No approvals, expiries or failures need you.</div>
            </div>
          ) : (
            notes.map((n, i) => (
              <button key={i} onClick={() => { onClose(); navigate(n.to); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px', borderBottom: '1px solid var(--border)', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: n.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={n.icon} size={15} color={n.color} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{n.title}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.sub}</span>
                </span>
                <Icon name="arrow-right" size={13} color="var(--text-muted)" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationsDrawer;
